import Table, { PrimaryKeyValues } from "table/Table";
import TableColumn from "table/TableColumn";
import { ColumnClause, ColumnPath, FilterClause, LiteralClause, OperatorClause, OrdOperator, RootClause } from "./Filter";
import { QueryColumn } from "./QueryColumn";
import { ColumnType } from "./ColumnType";
import { SingleQueryColumn } from "./SingleQueryColumn";
import { Row } from "table/Row";


export enum OrderedBy {
    ASC,
    DESC,
    NA
}

export interface OrderedByEntry {
    column: QueryColumn; // TODO: Should this be TableColumn so we can order by stuff we can't see?
    orderedBy: OrderedBy;
}


/* A "Query" is the model for a data table on the screen. It contains all 
the state of a query, plus the contents of the cur t screen-full of data. 
*/
export default class MultipleQueryColumn extends QueryColumn {
    // The Table I'm based on.
    protected _table: Table;

    // Usually this is _table.columns. Table columns for sub-queries are different from the table. 
    // Deprecated field. Use _basedOn instead.
    private _tableColumns: TableColumn; // TODO: How is this different from _basedOn?

    // "select" is the list of currently visible columns.
    // It is a tree structure. Expandable columns can be expanded or not expanded.
    protected _select: QueryColumn[];

    // Which columns to sort by.
    private _orderBy: Array<OrderedByEntry>;

    private _filter: RootClause = new RootClause();

    // We also store the currently selected cell because multiple components listen to it.
    private _currentSelected: any[] = []; // The primary key of the selected cell.

    protected _isExpanded: boolean = false;
    private _version = 0; // So that React can detect changes.

    constructor(table: Table, basedOn?: TableColumn, parent?: MultipleQueryColumn) {
        super(basedOn, parent);
        this._table = table;
        this._tableColumns = table.columns;

        this._orderBy = [];

        // We use this super-column to contain a list of my actual visible columns.
        //this.removeAllColumns(); - TypeScript compiler complains about this.
        this._select = [];
    }

    copyFrom: (other: MultipleQueryColumn) => MultipleQueryColumn = (other) => {
        this.name = other.name;
        this._select = other._select;
        this._orderBy = other._orderBy;
        this._filter = other._filter;
        this.renumberColumns();
        return this;
    }

    copy = () => {
        let result = new MultipleQueryColumn(this._table, this._basedOn, this._parent);
        return result.copyFrom(this);
    }

    get select(): QueryColumn[] {
        return this._select;
    }

    // TODO: don't use getters.
    getOrderBy = () => {
        return this._orderBy;
    }

    // TODO: confusable with getOrderBy. 
    orderBy = (column: QueryColumn, by: OrderedBy) => {
        this._orderBy = [{ column: column, orderedBy: by }];
        this.changed();
        return this;
    }


    replaceFilter = (column: TableColumn, value: string) => {
        let n = new OperatorClause(
            new ColumnClause([column]),
            OrdOperator.equals,
            new LiteralClause(value, column.type)
        );
        this._filter.clause = n;
    }

    findColumnByPath: (value: string) => QueryColumn | undefined =
        (value) => {
            return this.columns.find(each => each.name === value)
        }


    findTableColumnByPath: (value: string) => TableColumn =
        (value) => {
            // TODO: full paths separated with "/"
            let result = this._table.columns.find(each => each.name === value)
            if (undefined === result) {
                throw new Error("Column not found")
            } else {
                return result;
            }
        }

    /* Move the column c to position expandedColumnsIndex.
       -1 means to move to the first position.
       0 means to move to after the first column.
       etc.
       Note that the position is in the expandedColumns().
        */
    moveColumn = (from: QueryColumn, expandedColumnsIndex: number) => {

        if (expandedColumnsIndex === -1) {
            // before the first column
            let fromIndex = this._select.findIndex((each) => each === from);

            if (this._select[0] === from) return;
            this._select.unshift(from);
            fromIndex++;
            this._select.splice(fromIndex, 1);
            from.parent = this;
        } else {
            let toParent: MultipleQueryColumn;
            let toAfter: QueryColumn;
            toAfter = this.expandedColumns[expandedColumnsIndex];

            if (toAfter.parent !== undefined) { // It never will be.
                toParent = toAfter.parent;
            } else {
                throw new Error();
            }

            let toAfterAfter = this.expandedColumns[expandedColumnsIndex + 1];
            let toIndex: number;
            if (toAfter.parent !== toAfterAfter?.parent) {
                toParent = this;
                toIndex = this.rootIndexOf(toAfter);
            } else {
                toIndex = toParent.columns.findIndex((each) => each === toAfter);
            }
            let fromIndex = from.parent?.columns.findIndex((each) => each === from);
            if (fromIndex === undefined) {
                throw new Error();
            }

            if (-1 === toIndex) return;
            if (from.parent !== toParent && toParent !== this) {
                return;
            }
            if (fromIndex === toIndex && from.parent === toParent) return;
            toParent.columns.splice(toIndex + 1, 0, from);
            if (fromIndex > toIndex + 1) {
                fromIndex++;
            }
            from.parent?.columns.splice(fromIndex, 1);
            from.parent = toParent;
        }
        this.renumberColumns();
        this.changed();
    }

    // Return minus-one based index of the root ancestor of that column. 
    private rootIndexOf: (c: QueryColumn) => number = (c) => {
        if (c?.parent?._basedOn?.type === ColumnType.RootColumn) {
            return c.parent.columns.findIndex((each) => each === c);
        } else {
            if (c.parent !== undefined) {
                return this.rootIndexOf(c.parent);
            }
        }
        return -1; // impossible.
    }

    // TODO: remove this
    get table(): Table {
        return this._table;
    }

    get filter(): RootClause {
        return this._filter;
    }

    public removeClause = (c: FilterClause) => {
        this._filter.removeClause(c);
    }

    /* TODO: Make this a lot better... base it on Jooq or something.
    
    We want to be able to do filters like this:

    https://localhost:8443/o/northwind/Territories?$expand=Region&$select=TerritoryId&$filter=Region/RegionId eq 3

    q.where()

    */
    public whereEquals(columnPath: string, value: string) {
        let tc: ColumnPath|null = this._table.findColumnByPath(columnPath);
        if (null!==tc) {
            this._filter.and(tc, OrdOperator.equals, value);
        } else {
            throw new Error(`Column not found: ${columnPath}`);
        }
        return this;
    }

    /* Don't use me unless I own you. */
    public changed = () => {
        this._version = this._version + 1;
    }

    // Used to hack React's state chan1ge detection.
    public get version() {
        return this._version;
    }

    /* As a column with other columns below it, how many columns wide am I on the UI? */
    columnsAcross = () => {
        if (!this.isExpanded) {
            return 1;
        }

        let totalWidth = 0;
        for (let i = 0; i < this._select.length; i++) {
            let current = this._select[i].columnsAcross();
            totalWidth = totalWidth + current;

        }
        return totalWidth;
    }

    /* How many expanded columns are below me. NOT how deep I am. */
    depth(): number {
        if (!this.isExpanded) {
            return 1;
        }

        let maxDepth = 0;
        for (let i = 0; i < this._select.length; i++) {
            if (this._select[i].isExpanded) {
                let current = this._select[i].depth();
                if (current > maxDepth) {
                    maxDepth = current;
                }
            }
        }
        return maxDepth + 1;
    }

    /** Add all top-level columns of the table to this query.
     * No sub-columns are added because they might contain infinite loops. You 
     * still need to add these manually if you want them.
     * 
     * I can be used to add all sub-columns of a parent column as well.
     * 
     * TODO: add columns as a user expands them.
     */
    public allColumns = () => {
        if (!this._basedOn) {
            throw new Error();
        }
        for(let each of this._basedOn.children) {
            this.privateAddColumn(each);
        }
        this.renumberColumns();
        this.changed();
        return this;
    }

    addColumn = (c: TableColumn) => {
        this.privateAddColumn(c);
        this.renumberColumns();
        this.changed();
        return this;
    }

    private privateAddColumn = (c: TableColumn) => {
        // If the column already exists, don't add it again.
        let existingColumn = this.findColumnByPath(c.name);
        if (undefined!==existingColumn) {
            return existingColumn;
        }

        if (c.isComplex) { 
            // See also addSubColumnByName(...)
            let addMe: MultipleQueryColumn;
            addMe = new MultipleQueryColumn(this._table, c, this);
            addMe.parent = this;
            this._select.push(addMe);
            return addMe;
        } else {
            let addMe: QueryColumn;
            addMe = new SingleQueryColumn(c, this);
            addMe.parent = this;
            this._select.push(addMe);
            return addMe;
        }
    }

    /** Expand this column. If I already have children, I just set my 'expanded' attribute to true. 
     * If I have no children, all possible children will be added.
     */
    public expand = () => {
        if (0===this._select.length) {
            this.allColumns();
        }
        this._isExpanded = true;
    }

    public unexpand = () => {
        this._isExpanded = false;
    }

    /*
    addColumnByPath = (p: string) => {
        if (p.length === 0) {
            throw new Error("Path was empty.");
        }

        console.log(`Looking for ${p}`);
        console.log(this._tableColumns);

        this.addColumnByPathImpl(p, this._select, this._tableColumns);
        return this;
    }

    private addColumnByPathImpl: (path: string, parent: QueryColumn, tableParent: TableColumn | null) => QueryColumn
        = (path, parent, tableParent) => {
            if (path.length > 0) {
                (parent as MultipleQueryColumn).setExpanded(true);
            } else { 
                return parent;
            }

            if (!tableParent) {
                throw new Error(`Could not find column in table: ${path}`);
            }

            let nextColumnName: string;
            let remainingPath: string;

            if (path.indexOf("/") > 0) {
                let split = path.indexOf("/");
                nextColumnName = path.substring(0, split);
                remainingPath = path.substring(split + 1);
            } else {
                nextColumnName = path;
                remainingPath = "";
            }

            // Re-use any parents that already exist in this query.
            for (let each of parent.columns) {
                if (each._basedOn.name === nextColumnName) {
                    return this.addColumnByPathImpl(remainingPath, each, tableParent.findColumn(nextColumnName));
                }
            }

            // It wasn't found. It's a new column hierarchy for this query.
            let tc: TableColumn | null = tableParent.findColumn(nextColumnName);
            if (tc) {
                if (parent instanceof MultipleQueryColumn) {
                    let newColumn = (parent as MultipleQueryColumn).addColumn(tc);
                    return this.addColumnByPathImpl(remainingPath, newColumn, tc);
                } else {
                    throw new Error(`${nextColumnName} does not have child columns.`);
                }
            }
            throw new Error(`Could not find path ${path}`);
        }


    */

    public addColumnByName = (name: string) => {
        let column = this._tableColumns.findColumn(name);
        if (!column) {
            throw new Error(`Could not find column ${name} in ${this.table.name}`);
        }
        this.privateAddColumn(column);
        this.renumberColumns();
        this.changed();
        return this;
    }

    public addColumnsByName = (names: string[]) => {
        for (let name of names) {
            let column = this._tableColumns.findColumn(name);
            if (!column) {
                throw new Error(`Could not find column ${name} in ${this.table.name}`);
            }
            this.privateAddColumn(column);
            this.renumberColumns();
            this.changed();
        }
        return this;
    }

    public addSubColumnByName(name: string, isExpanded: boolean, withSubColumns: (q: MultipleQueryColumn) => void) {
        let tc: TableColumn | null;
        tc = this._tableColumns.findColumn(name);
        if (tc) {
            if (!tc.isComplex) {
                throw new Error(`Column ${name} cannot be expanded.`);
            }

            let subquery = new MultipleQueryColumn(this._table, tc, this);
            subquery.tableColumns = tc;
            subquery.parent = this;
            this._select.push(subquery);
            this.renumberColumns();
            withSubColumns(subquery);
            if (isExpanded) {
                subquery.expand();
            }
        } else {
            throw new Error(`Could not find table column ${name}.`);
        }
        return this;
    }

    set tableColumns(tc: TableColumn) {
        this._tableColumns = tc;
    }

    removeColumn = (c: QueryColumn) => {
        let index = this._select.indexOf(c);
        if (index > -1) {
            this._select.splice(index, 1);
        }
        this.changed();
    }

    removeColumnAt = (index: number) => {
        this._select.splice(index, 1);
        this.changed();
    }

    map<U>(callbackfn: (value: QueryColumn, index: number, array: QueryColumn[]) => U, thisArg?: any): U[] {
        return this._select.map(callbackfn);
    }

    isEmpty(): boolean {
        return this._select.length === 0;
    }

    canHaveChildren(): boolean {
        return true;
    }

    // TODO: use columns() instead
    children() {
        return this._select;
    }

    get columns(): Array<QueryColumn> {
        return this._select;
    }

    set columns(c: Array<QueryColumn>) {
        this._select = c;
    }

    numColumns(): number {
        let sum = 0;
        for (let i = 0; i < this._select.length; i++) {
            sum = sum + this._select[i].numColumns();
        }
        return Math.max(1, sum);
    }

    renumberColumnsImpl = (from: number[]) => {
        this._columnNumber = from[0];
        if (this.isExpanded) {
            for (let each of this._select) {
                each.renumberColumnsImpl(from);
            }
        } else {
            from[0] = from[0] + 1;
        }
    }

    /* Return a version of the columns which includes all the expanded ones. 
      TODO: remove this method. We don't expand columns like this any more. */
    get expandedColumns(): QueryColumn[] {
        let result: QueryColumn[] = [];
        this.expandedColumnsImpl(this, result);
        return result;
    }

    private expandedColumnsImpl = (c: QueryColumn, result: QueryColumn[]) => {
        if (c.numColumns() > 0 && c.isExpanded) {
            for (let each of c.columns) {
                this.expandedColumnsImpl(each, result);
            }
        }
        else {
            result.push(c);
        }
    }

    public get isExpanded() {
        return this._isExpanded;
    }

    public primaryKey = () => {
        if (!this._table) {
            throw new Error("Table not intialised.");
        }
        return this._table.primaryKey;
    }

    private primaryKeyValuesFor = (row : Row) => {
        
        /* The first column(s) of this._select are the primary key values for each row. */
        let result : PrimaryKeyValues = new PrimaryKeyValues();
        for (let i=0; i<this.table.primaryKey.length(); i++) {
            let pkColumn = this._select[i];
            let value = row.cells[i];
            if (!pkColumn._basedOn) {
                throw new Error();
            }
            result.set(pkColumn._basedOn, value._value);
        }
        return result;
    }

}
